import { buildMatchers, matchStory } from "./news.js";

// Newsstand extras (design_documents/news.md §2, §8, §9): per-device settings
// (sources / categories switched off, the opt-in bell), Clippings (☆ saved
// stories), and the library matches behind "In the news" and the bell notices.

const PREFS_KEY = "newsstand:prefs";
const CLIPS_KEY = "newsstand:clippings";
const BELL_DAY_KEY = "newsstand:bellDay";
const CLIPS_CAP = 200;
export const BELL_PER_DAY = 3;

const DEFAULT_PREFS = { off_sources: [], off_categories: [], bell: false };

function read(key, fallback) {
	try {
		const raw = localStorage.getItem(key);
		return raw ? JSON.parse(raw) : fallback;
	} catch {
		return fallback;
	}
}

function write(key, value) {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// storage unavailable — the setting just won't stick
	}
}

export function loadNewsPrefs() {
	return { ...DEFAULT_PREFS, ...read(PREFS_KEY, {}) };
}

export function saveNewsPrefs(prefs) {
	write(PREFS_KEY, { ...DEFAULT_PREFS, ...prefs });
}

// Flip one source / category on or off. → the new prefs.
export function toggleOff(prefs, field, value) {
	const set = new Set(prefs[field] ?? []);
	if (set.has(value)) set.delete(value);
	else set.add(value);
	return { ...prefs, [field]: [...set] };
}

// The stories the reader wants: switched-off sources and categories dropped.
export function applyNewsPrefs(items, prefs) {
	const offS = new Set(prefs?.off_sources ?? []);
	const offC = new Set(prefs?.off_categories ?? []);
	return (items ?? []).filter((s) => !offS.has(s.source) && !offC.has(s.category));
}

// ---------- Clippings ----------

export function listClippings() {
	return read(CLIPS_KEY, []);
}

export function isClipped(id) {
	return listClippings().some((c) => c.id === id);
}

// Save or un-save a story. → true when it's now clipped.
export function toggleClipping(story) {
	const clips = listClippings();
	const i = clips.findIndex((c) => c.id === story.id);
	if (i >= 0) {
		clips.splice(i, 1);
		write(CLIPS_KEY, clips);
		return false;
	}
	const { id, title, summary, link, image, source, category, published, review } = story;
	clips.unshift({ id, title, summary, link, image, source, category, published, review: !!review, clipped_at: new Date().toISOString() });
	write(CLIPS_KEY, clips.slice(0, CLIPS_CAP));
	return true;
}

// ---------- library matches ----------

// Stories that mention one item (its detail page's "In the news").
export function storiesAbout(item, stories, limit = 3) {
	const matchers = buildMatchers({ library: [item] });
	if (!matchers.length) return [];
	return (stories ?? []).filter((s) => matchStory(s, matchers)).slice(0, limit);
}

// Bell notices for news about library items (opt-in): at most BELL_PER_DAY a
// day, never the same story twice (ids dedupe in the bell).
// → [{ notice }] given how many were already posted today.
export function newsNoticesFor(stories, library, { postedToday = 0, now = new Date().toISOString() } = {}) {
	const room = Math.max(0, BELL_PER_DAY - postedToday);
	if (!room) return [];
	const matchers = buildMatchers({ library });
	const out = [];
	for (const s of stories ?? []) {
		if (out.length >= room) break;
		const m = matchStory(s, matchers);
		if (!m || m.kind !== "library") continue;
		out.push({
			id: `news:${s.id}`,
			type: "news",
			title: `News about ${m.label}`,
			body: `${s.title} — ${s.source}`,
			route: m.route ?? "/news",
			cover_url: s.image ?? null,
			media_type: m.media_type ?? null,
			created_at: now,
			read: false,
		});
	}
	return out;
}

// How many news notices the bell got today (per device).
export function bellCountToday(today) {
	const rec = read(BELL_DAY_KEY, null);
	return rec?.day === today ? rec.count : 0;
}

export function addBellCount(today, n) {
	write(BELL_DAY_KEY, { day: today, count: bellCountToday(today) + n });
}
